import { useEffect, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { useParams, NavLink } from 'react-router-dom';
import useRerender from '../../Common/components/hooks/useRerender';
import Table, { createColumnHelper } from '../../Common/components/Table';
import useDeviceKeys from './hooks/useDeviceKeys';
import moment from 'moment-timezone';
import useUserOrg from '../../Authentication/components/hooks/useUserOrg';
import GroupDeviceKeyActions from './GroupDeviceKeyActions';
import { Button } from '@/src/shadComponents/ui/button';

const columnHelper = createColumnHelper<DeviceKey>();

export default function GroupDeviceKeys() {
    const { groupId } = useParams();
    const { orgId } = useUserOrg();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [deviceKeys, setDeviceKeys] = useState<DeviceKey[]>([]);
    const [group, setGroup] = useState<DeviceGroup | null>(null);
    const { getDeviceKeysForGroup } = useDeviceKeys(orgId);

    const { render, renderCount } = useRerender();

    const onDelete = () => {
        render();
    };
    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            try {
                const deviceKeys = await getDeviceKeysForGroup(+(groupId || 0));
                setDeviceKeys(deviceKeys.keys);
                setGroup(deviceKeys.group);
            } catch (e) {
                console.error(e);
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        load();

        return () => {
            isMounted = false;
        };
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [renderCount]);

    const columnDefinitions = [
        columnHelper.accessor('name', {
            id: 'name',
            header: () => <span>name</span>,
            cell: (info) => <span>{info.getValue()}</span>,
        }),
        columnHelper.accessor('keyId', {
            id: 'keyId',
            header: () => <span>keyId</span>,
            cell: (info) => <span>{info.getValue()}</span>,
        }),
        columnHelper.accessor('lastUsed', {
            id: 'lastUsed',
            header: () => <span>last seen</span>,
            cell: (info) => (
                <span>
                    {info.getValue()
                        ? `${moment(info.getValue()).toNow(true)} ago`
                        : ''}
                </span>
            ),
        }),
        columnHelper.display({
            id: 'actions',
            cell: (props: { row: any }) => (
                <GroupDeviceKeyActions
                    orgId={orgId}
                    row={props.row}
                    groupId={groupId ? +groupId : 0}
                    onDelete={onDelete}
                />
            ),
        }),
    ];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div>
            <div className="flex items-center mx-8 my-4">
                <div>
                    <p className="text-sm text-muted-foreground">
                        Device Group
                    </p>
                    <h1 className="mt-1 text-2xl font-bold tracking-tight text-foreground">
                        {group?.name}
                    </h1>
                </div>
                <div className="flex-1" />
                <div className="flex items-center gap-2">
                    <NavLink
                        to={`/device-management/device-groups/${groupId}/device-keys/assign`}
                    >
                        <Button variant="outline">
                            <Plus className="size-4" />
                            Assign Device Key
                        </Button>
                    </NavLink>
                    <NavLink
                        to={`/device-management/device-groups/${groupId}/device-keys/create`}
                    >
                        <Button>
                            <Plus className="size-4" />
                            Create New Device Key
                        </Button>
                    </NavLink>
                </div>
            </div>
            <Table data={deviceKeys} columns={columnDefinitions} />
        </div>
    );
}
